/**
 * The single icon map (§11 "Iconography").
 *
 * lucide-react exclusively, imported by name here and nowhere else. That keeps
 * the icon set consistent, makes the bundle tree-shakeable, and means the
 * server can send an icon *name* in the Smart Action panel that the client
 * resolves through `iconByName` — without shipping all 1,500 lucide icons.
 */
import {
  AlertCircle,
  ArrowRight,
  Award,
  Ban,
  BellOff,
  BellRing,
  BookOpen,
  Briefcase,
  Building2,
  CalendarCheck2,
  CalendarDays,
  CalendarClock,
  CalendarPlus,
  Check,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  ClipboardCheck,
  Clock,
  Copy,
  CreditCard,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileCheck2,
  FileDown,
  FileImage,
  FilePlus2,
  FileText,
  Filter,
  Globe,
  GraduationCap,
  History,
  IndianRupee,
  Inbox,
  Info,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  Loader2,
  Lock,
  LogOut,
  Mail,
  MailCheck,
  MailQuestion,
  MailWarning,
  MapPin,
  MessageCircle,
  MessageSquare,
  MoreVertical,
  Newspaper,
  Package,
  PackageCheck,
  PartyPopper,
  PenLine,
  Phone,
  PhoneCall,
  PlayCircle,
  Plus,
  Printer,
  Receipt,
  ReceiptIndianRupee,
  RefreshCw,
  RotateCcw,
  Scale,
  ScanBarcode,
  Search,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  SlidersHorizontal,
  Star,
  StickyNote,
  Timer,
  Trash2,
  TrendingUp,
  Truck,
  Undo2,
  Upload,
  Wallet,
  User,
  UserPlus,
  UserRoundCheck,
  Users,
  X,
  XCircle,
  Zap,
  type LucideIcon,
} from 'lucide-react';

export const Icons = {
  AlertCircle,
  ArrowRight,
  Award,
  Ban,
  BellOff,
  BellRing,
  BookOpen,
  Briefcase,
  Building2,
  CalendarCheck2,
  CalendarDays,
  CalendarClock,
  CalendarPlus,
  Check,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  ClipboardCheck,
  Clock,
  Copy,
  CreditCard,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileCheck2,
  FileDown,
  FileImage,
  FilePlus2,
  FileText,
  Filter,
  Globe,
  GraduationCap,
  History,
  IndianRupee,
  Inbox,
  Info,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  Loader2,
  Lock,
  LogOut,
  Mail,
  MailCheck,
  MailQuestion,
  MailWarning,
  MapPin,
  MessageCircle,
  MessageSquare,
  MoreVertical,
  Newspaper,
  Package,
  PackageCheck,
  PartyPopper,
  PenLine,
  Phone,
  PhoneCall,
  PlayCircle,
  Plus,
  Printer,
  Receipt,
  ReceiptIndianRupee,
  RefreshCw,
  RotateCcw,
  Scale,
  ScanBarcode,
  Search,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  SlidersHorizontal,
  Star,
  StickyNote,
  Timer,
  Trash2,
  TrendingUp,
  Truck,
  Undo2,
  Upload,
  Wallet,
  User,
  UserPlus,
  UserRoundCheck,
  Users,
  X,
  XCircle,
  Zap,
} as const;

export type IconName = keyof typeof Icons;

/**
 * Resolve an icon name that arrived from the server (Smart Action panel,
 * restriction flags). Falls back rather than crashing the panel if the API
 * ever names an icon this build doesn't carry.
 */
export function iconByName(name: string | null | undefined): LucideIcon {
  if (!name) return Icons.Info;
  return (Icons as Record<string, LucideIcon>)[name] ?? Icons.Info;
}

/** Fixed size tokens (§11: 16/18/20px, stroke 1.75). */
export const ICON_SIZE = { sm: 16, md: 18, lg: 20 } as const;
export const ICON_STROKE = 1.75;

export type { LucideIcon };
